import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Send, Crosshair } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Reveal, SectionHeading } from "./Reveal";

const facilities = ["Warehouse & Logistics", "Construction Site", "Corporate Campus", "Retail Centre", "Private Estate", "Industrial Plant"];
const threats = ["Standard", "Elevated", "Critical"];

const serviceOptions = [
  { id: "cctv", label: "CCTV & Thermal Optics", weight: 1 },
  { id: "fob", label: "Key Fob Access", weight: 1 },
  { id: "patrols", label: "Mobile Patrols", weight: 2 },
  { id: "static", label: "Static Guards", weight: 2 },
  { id: "k9", label: "K9 Units", weight: 3 },
  { id: "compound", label: "Compound K9s", weight: 3 },
  { id: "alarm", label: "Alarm Response", weight: 2 },
  { id: "consulting", label: "Risk Consulting", weight: 1 },
];

const initialForm = { name: "", email: "", phone: "", company: "", message: "" };

export default function QuoteForm() {
  const [facility, setFacility] = useState(facilities[0]);
  const [threat, setThreat] = useState(threats[0]);
  const [selected, setSelected] = useState(["cctv"]);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const responseLevel = useMemo(() => {
    const threatScore = threats.indexOf(threat) * 2;
    const serviceScore = selected.reduce((sum, id) => sum + (serviceOptions.find((s) => s.id === id)?.weight || 0), 0);
    const total = threatScore + serviceScore;
    if (total >= 10) return { code: "DELTA", label: "Critical Response", color: "text-blue-400 border-blue-500/50 bg-blue-500/10" };
    if (total >= 6) return { code: "ALPHA", label: "Elevated Cover", color: "text-amber-400 border-amber-500/50 bg-amber-500/10" };
    return { code: "BRAVO", label: "Standard Cover", color: "text-emerald-400 border-emerald-500/50 bg-emerald-500/10" };
  }, [threat, selected]);

  const toggleService = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) {
      toast.error("Select at least one required capability.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        ...form,
        facility_type: facility,
        threat_level: threat,
        services: selected.map((id) => serviceOptions.find((s) => s.id === id).label),
        response_level: `${responseLevel.code} — ${responseLevel.label}`,
      });
      toast.success("Deployment request received. Our command desk responds within 24 hours.");
      setForm(initialForm);
      setSelected(["cctv"]);
      setThreat(threats[0]);
      setFacility(facilities[0]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#08090B] border border-white/10 focus:border-blue-500/60 outline-none px-4 py-3 text-sm text-gray-100 placeholder:text-gray-600 transition-colors duration-300";

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute right-0 top-0 w-[480px] h-[480px] rounded-full bg-blue-600/10 blur-[140px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          testid="quote-heading"
          overline="Deployment Request"
          title="Build Your Security Posture"
          description="Configure your facility profile below. The estimator computes a recommended response level instantly — then transmit it straight to our command desk."
        />

        <form data-testid="quote-form" onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal className="clip-corners bg-[#0F1115] border border-white/10 p-8 flex flex-col gap-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500 mb-4">01 / Facility Type</p>
              <div className="flex flex-wrap gap-2">
                {facilities.map((f) => (
                  <button
                    type="button"
                    key={f}
                    data-testid={`facility-option-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setFacility(f)}
                    className={`clip-corners px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                      facility === f ? "bg-blue-600 border-blue-500 text-white" : "border-white/10 text-gray-400 hover:border-blue-500/40 hover:text-gray-200"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500 mb-4">02 / Threat Level</p>
              <div className="grid grid-cols-3 gap-2">
                {threats.map((t) => (
                  <button
                    type="button"
                    key={t}
                    data-testid={`threat-option-${t.toLowerCase()}`}
                    onClick={() => setThreat(t)}
                    className={`clip-corners px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                      threat === t ? "bg-blue-600 border-blue-500 text-white" : "border-white/10 text-gray-400 hover:border-blue-500/40 hover:text-gray-200"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500 mb-4">03 / Required Capabilities</p>
              <div className="grid grid-cols-2 gap-2">
                {serviceOptions.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    data-testid={`service-option-${s.id}`}
                    onClick={() => toggleService(s.id)}
                    className={`clip-corners px-4 py-3 font-mono text-xs uppercase tracking-[0.1em] border text-left transition-colors duration-300 ${
                      selected.includes(s.id) ? "bg-blue-600/15 border-blue-500/60 text-blue-300" : "border-white/10 text-gray-400 hover:border-blue-500/40 hover:text-gray-200"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div data-testid="response-level-readout" className={`clip-corners border p-5 flex items-center gap-4 ${responseLevel.color}`}>
              <Crosshair className="w-8 h-8 shrink-0" />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Recommended Posture</p>
                <p className="font-display font-extrabold text-xl uppercase tracking-tight">
                  {responseLevel.code} — {responseLevel.label}
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="clip-corners bg-[#0F1115] border border-white/10 p-8 flex flex-col gap-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500">04 / Contact Coordinates</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="quote-input-name" required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="quote-input-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="quote-input-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
              <input data-testid="quote-input-company" placeholder="Company / Site" value={form.company} onChange={set("company")} className={inputCls} />
            </div>
            <textarea
              data-testid="quote-input-message"
              placeholder="Brief us on the site — acreage, operating hours, known incidents, current provider..."
              value={form.message}
              onChange={set("message")}
              rows={7}
              className={`${inputCls} resize-none`}
            />
            <div className="flex items-start gap-3 text-gray-500 text-xs font-mono leading-relaxed">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
              Transmissions are encrypted and reviewed by a senior assessor — never a call centre.
            </div>
            <button
              data-testid="quote-submit-button"
              type="submit"
              disabled={submitting}
              className="clip-corners bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-mono text-sm uppercase tracking-[0.2em] px-8 py-4 flex items-center justify-center gap-3 transition-colors duration-300 mt-auto"
            >
              {submitting ? "Transmitting..." : "Transmit Request"}
              <Send className="w-4 h-4" />
            </button>
          </Reveal>
        </form>
      </div>
    </section>
  );
}
